/**
 * O avatar de uma pessoa: a foto, quando ela pôs uma; senão, as iniciais
 * (cor da família, ou a cor de reserva). Um componente só, para a foto
 * aparecer em todo lugar onde havia iniciais.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { iniciais } from '../mapa/regras';
import { enviarVariante } from '../memorias/api';
import { fotoReduzida } from '../memorias/imagem';

export function FotoAvatar({ nome, url, cor, tamanho = 34, className = '' }: { nome: string; url?: string | null | undefined; cor?: string | null | undefined; tamanho?: number; className?: string }) {
  const estilo = { width: tamanho, height: tamanho, fontSize: Math.round(tamanho * 0.38), ...(cor && !url ? { background: cor } : {}) };
  return (
    <span className={`grupo__avatar rk__avatar foto-avatar ${url ? 'foto-avatar--foto' : ''} ${className}`} style={estilo} aria-hidden="true">
      {url ? <img src={url} alt="" loading="lazy" /> : iniciais(nome)}
    </span>
  );
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

/** A minha foto (URL assinada; nula sem foto). */
export function useMeuAvatar(ligado = true) {
  return useQuery({ queryKey: ['meu-avatar'], queryFn: () => pedir<{ url: string | null }>('/api/v1/me/avatar'), enabled: ligado, staleTime: 5 * 60_000 });
}

/** Trocar (reduzida a 512 px no aparelho) ou tirar a foto. */
export function useTrocarAvatar() {
  const cliente = useQueryClient();
  const depois = () => {
    void cliente.invalidateQueries({ queryKey: ['meu-avatar'] });
    void cliente.invalidateQueries({ queryKey: ['viagem'] });
  };
  const trocar = useMutation({
    mutationFn: async (arquivo: File) => {
      const reduzida = await fotoReduzida(arquivo, 512, 0.85);
      const pedido = await pedir<{ key: string; upload: { url: string; method: 'PUT'; headers: Record<string, string> } }>('/api/v1/me/avatar', { method: 'POST', body: JSON.stringify({ mimeType: 'image/jpeg', bytes: reduzida.size }) });
      await enviarVariante(pedido.upload, reduzida);
      return pedir<{ url: string }>('/api/v1/me/avatar/complete', { method: 'POST', body: JSON.stringify({ key: pedido.key, mimeType: 'image/jpeg' }) });
    },
    onSuccess: depois,
  });
  const tirar = useMutation({ mutationFn: () => pedir<{ ok: true }>('/api/v1/me/avatar', { method: 'DELETE' }), onSuccess: depois });
  return { trocar, tirar };
}
